import { formatSerial } from '@/utils/ui/serial';
import { supabase } from '@/service/supabaseClient';
import { AssetTypes } from './AssetTypes';

// Interface for the records from last_device_values view
export interface InboundRecord {
  node_id: string;
  asset_id: string;
  device_id: string;
  device_name: string | null;
  /** Readable name set by the company owner. Null when nobody renamed it. */
  alias?: string | null;
  message_type: string | null;
  schema_version: string | null;
  received_at: string; // ISO date string
  payload: Record<string, unknown>;
  content: string | null;
  content_type: string | null;
}

export abstract class GenericAsset {
  protected abstract supportedMessageTypes: string[];

  // Identifiers
  node_id: string;
  asset_id: string;
  device_id: string;
  /**
   * The machine's own identifier, written by the edge from
   * POC_INJECTOR_DEVICE_NAME and rewritten at every heartbeat. It is the
   * serial, not a label: changing it on the machine creates a new asset.
   */
  device_name: string | null;
  /**
   * Readable name, set from the dashboard by the company owner and stored in
   * device_registry. Never touched by the machine, so it survives restarts and
   * the retention on inbound. Null until somebody sets one.
   */
  alias: string | null;
  /**
   * La versione dell'injector che scrive per questa macchina, letta da
   * device_registry.properties e non dal heartbeat.
   *
   * Non e un dato del robot: il heartbeat riporta cio che il robot dichiara di
   * se, e la versione del servizio che sta all'altro capo del cavo e un'altra
   * cosa. Sta nel registro anche perche li e un upsert - una riga per macchina,
   * riscritta - invece di una costante ripetuta in ogni heartbeat per sempre.
   *
   * Null per una macchina con un injector piu vecchio, che quel campo non lo
   * scrive: e un'informazione anche quella.
   */
  injectorVersion: string | null;

  /**
   * Il modello commerciale, dichiarato nel deploy.env della macchina e letto da
   * device_registry.
   *
   * Non viene dal heartbeat di proposito: li' dentro c'e' il modello con cui il
   * software di bordo e' stato compilato, che sulle macchine installate oggi e'
   * un segnaposto e per cambiarlo bisogna aggiornarle una per una. Questo lo sa
   * chi installa, e vale dal riavvio successivo.
   *
   * Null quando nessuno l'ha configurato: in quel caso si mostra cio' che
   * dichiara la macchina, esattamente come prima.
   */
  model: string | null;

  // Payload
  payload: Record<string, unknown> | null;

  // Inbound record
  inboundRecords: InboundRecord[];

  // General Status & Info
  public lastMessageReceived: Date | null;
  public lastMessageType: string | null;

  // Asset image path
  protected _assetImagePath: string;

  constructor(InboundRecords: InboundRecord[]) {
    this.inboundRecords = InboundRecords;
    this.asset_id = InboundRecords[0].asset_id;
    this.node_id = InboundRecords[0].node_id;
    this.device_id = InboundRecords[0].device_id;
    this.device_name = InboundRecords[0].device_name;

    this.payload = InboundRecords[0].payload;

    this.lastMessageReceived = null;
    this.lastMessageType = InboundRecords[0].message_type;

    this.asset_id = InboundRecords[0].asset_id;
    this.node_id = InboundRecords[0].node_id;
    this.device_id = InboundRecords[0].device_id;
    this.device_name = InboundRecords[0].device_name;
    this.alias = InboundRecords[0].alias ?? null;
    // Arriva dal registro, non dai record: lo store la applica dopo il
    // caricamento, come fa con l'alias.
    this.injectorVersion = null;
    this.model = null;
    this._assetImagePath = '/map/asset-image-not-found2.png';
  }

  /**
   * What to show wherever a human reads the machine's name.
   *
   * Falls back to the serial, which is what every screen showed before aliases
   * existed: a machine nobody has renamed must look exactly as it did.
   */
  public get displayName(): string {
    const alias = this.alias?.trim();
    return alias && alias.length > 0 ? alias : formatSerial(this.device_name);
  }

  public getSupportedMessageTypes(): string[] {
    return this.supportedMessageTypes;
  }

  /**
   * Get the path to the asset's image.
   */
  public get assetImagePath(): string {
    return this._assetImagePath;
  }

  /**
   * Set the path to the asset's image.
   * @param path The new image path.
   */
  public set assetImagePath(path: string) {
    this._assetImagePath = path;
  }

  public abstract updateInboundRecord(records: InboundRecord[]): void;

  /**
   * Gets the current status of the asset
   */
  public abstract getStatus(): unknown;

  /**
   * Checks if the asset is online based on recent heartbeat
   */
  public abstract isOnline(): boolean;

  public getAssetType(): string {
    return AssetTypes.UNKNOWN;
  }

  public static getMessageTypes(): string[] {
    return [];
  }

  /**
   * Gets the history of a property for the asset, from inbound table
   * @param property The property to get the history of
   * @returns An array of objects containing the id, asset_id, payload, and timestamp
   */
  async getPropertyHistory(
    limit = 100,
    filterByMessageType: string[] | null = null,
    range: { from?: Date | null; to?: Date | null } | null = null,
  ): Promise<Array<{ message_type: string; payload: Record<string, unknown>; timestamp: Date }>> {
    if (limit <= 0) {
      console.warn('getPropertyHistory: limit is less than or equal to 0, returning empty array');
      return [];
    }
    let query = supabase
      .from('inbound')
      .select('received_at, message_type, payload')
      .eq('node_id', this.node_id)
      .eq('asset_id', this.asset_id)
      .eq('device_id', this.device_id)
      .in('message_type', filterByMessageType ?? this.supportedMessageTypes);

    /**
     * L'intervallo lo applica il DATABASE, non il browser.
     *
     * Prima si scaricavano le ultime cento righe e si filtrava qui: allargare
     * l'intervallo non poteva far arrivare piu' dati, poteva solo mostrarne di
     * meno. Chi chiedeva un mese vedeva meno punti di chi chiedeva un'ora, e in
     * database c'era tutto.
     */
    if (range?.from) query = query.gte('received_at', range.from.toISOString());
    if (range?.to) query = query.lte('received_at', range.to.toISOString());

    const { data, error } = await query.order('received_at', { ascending: false }).limit(limit);

    if (error) {
      console.error('Error fetching property history:', error);
      return [];
    }

    return (
      data?.map((item) => ({
        message_type: item.message_type,
        payload: item.payload as Record<string, unknown>,
        timestamp: new Date(item.received_at),
      })) ?? []
    );
  }

  /**
   * Exports generic asset properties (from GenericAsset)
   * For example, node_id, asset_id, device_id, device_name, etc.
   * @returns Object containing all generic asset properties with original property names
   */
  public exportGenericProperties(): Record<string, unknown> {
    return {
      asset_id: this.asset_id,
      node_id: this.node_id,
      device_id: this.device_id,
      device_name: formatSerial(this.device_name),
      status: this.getStatus(),
      lastMessageReceived: this.lastMessageReceived,
      lastMessageType: this.lastMessageType,
      isOnline: this.isOnline(),
    };
  }

  /**
   * Exports all asset properties (from GenericAsset)
   * For example, node_id, asset_id, device_id, device_name, specific_property, engine_rpm, torque, etc.
   * @returns Object containing all asset properties with original property names
   */
  public abstract exportAllProperties(): Record<string, unknown>;

  /**
   * Translates the key of an asset property to the user's language
   * @param key The key of the asset property to translate
   * @param t The translation function
   * @returns The translated key
   */
  public abstract translateExportKey(
    key: string,
    t?: (key: string, params?: Record<string, unknown>) => string,
  ): string;

  /**
   * Formats the value of an asset property for export
   * @param key The key of the asset property to format
   * @param value The value of the asset property to format
   * @param t The translation function
   * @returns The formatted value
   */
  public abstract formatExportValue(
    key: string,
    value: unknown,
    t?: (key: string, params?: Record<string, unknown>) => string,
  ): string;

  public abstract isCalculatedProperty(key: string): boolean;
}
